import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Grid } from '@mui/material';
import CowTeatPicker from './CowTeatPicker';
import { useDashboard } from '../../../contexts/DashboardContext';

type Teat = 'AE' | 'AD' | 'PE' | 'PD';

interface MastitisTeatStepProps {
  initialData?: any;
}

const MastitisTeatStep: React.FC<MastitisTeatStepProps> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [selected, setSelected] = useState<Teat[]>(
    Array.isArray(initialData?.mastitis?.teats) ? (initialData?.mastitis?.teats as Teat[]) : []
  );

  const handleNext = () => {
    const payload = {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        teats: selected,
      },
    };
    updateContent('mastitisAnimalIdStep', payload);
  };

  const handleCancel = () => updateContent('welcome');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Seleção de Teto</Typography>
        <Button variant="text" size="small" onClick={handleCancel} sx={{ color: 'text.secondary', textTransform: 'none' }}>
          Cancelar
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 2,
            width: '100%',
            maxWidth: 1080,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          })}
        >
          <Grid container spacing={2} alignItems="flex-start" sx={{ flexWrap: 'nowrap' }}>
            {/* Esquerda: descrição detalhada do teste e instruções */}
            <Grid size={6} sx={{ flexBasis: '45%', maxWidth: '45%' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                O teste de mastite tem como objetivo identificar inflamações ou infecções presentes no úbere da vaca, causadas por microrganismos que afetam a qualidade do leite e a saúde do animal.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                Nesta etapa, selecione quais tetos apresentam suspeita de mastite. Você pode marcar um ou mais tetos, conforme observação visual ou alteração detectada durante a ordenha.
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Os principais sinais de mastite incluem:</Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary' }}>
                <Box component="li"><Typography variant="body2">Leite com grumos, aspecto aquoso ou coloração alterada</Typography></Box>
                <Box component="li"><Typography variant="body2">Inchaço, calor ou vermelhidão no úbere</Typography></Box>
                <Box component="li"><Typography variant="body2">Dor ou sensibilidade ao toque</Typography></Box>
                <Box component="li"><Typography variant="body2">Redução na produção de leite</Typography></Box>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
                Após selecionar os tetos afetados, você poderá enviar as imagens do teste para identificação do possível agente causador e emissão de uma análise parcial e posteriormente final.
              </Typography>
            </Grid>

            {/* Direita: vaquinha para seleção de tetos */}
            <Grid size={6} sx={{ flexBasis: '55%', maxWidth: '55%', display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <Box sx={{ mt: 1, width: { xs: 260, md: 360 }, maxWidth: 480 }}>
                  <CowTeatPicker
                    backgroundSrc={'/vaca-pespective.svg'}
                    value={selected}
                    onChange={setSelected}
                    labels={{ AE: 'Teto anterior esquerdo', AD: 'Teto anterior direito', PE: 'Teto posterior esquerdo', PD: 'Teto posterior direito' }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Ações */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '100%', mt: 2 }}>
            <Button variant="outlined" onClick={handleCancel} sx={{ textTransform: 'none' }}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                color: '#fff !important',
                '&:hover': { color: '#fff !important' },
              }}
              disabled={selected.length === 0}
            >
              Próximo
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MastitisTeatStep;
